import { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { TrendingUp, PieChart as PieChartIcon, BarChart3 } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, LineChart, Line, Area, AreaChart
} from "recharts";

const COLORS = ["hsl(var(--primary))", "#22c55e", "#3b82f6", "#eab308", "#ef4444", "#8b5cf6", "#f97316"];

const planLabels: Record<string, string> = { free: "مجاني", basic: "أساسي", premium: "متقدم", enterprise: "مؤسسي" };
const statusLabels: Record<string, string> = { active: "نشط", expired: "منتهي", suspended: "معلق", cancelled: "ملغي" };

interface Props {
  subscriptions: any[];
}

const SubscriptionCharts = ({ subscriptions }: Props) => {
  const planDist = useMemo(() => {
    const counts: Record<string, number> = {};
    subscriptions.forEach((s) => { counts[s.plan] = (counts[s.plan] || 0) + 1; });
    return Object.entries(counts).map(([plan, count]) => ({ name: planLabels[plan] || plan, value: count }));
  }, [subscriptions]);

  const statusDist = useMemo(() => {
    const counts: Record<string, number> = {};
    subscriptions.forEach((s) => {
      const key = (s as any).is_trial ? "trial" : s.status;
      counts[key] = (counts[key] || 0) + 1;
    });
    const labels: Record<string, string> = { ...statusLabels, trial: "تجربة" };
    return Object.entries(counts).map(([status, count]) => ({ name: labels[status] || status, value: count }));
  }, [subscriptions]);

  const revenueByPlan = useMemo(() => {
    const map: Record<string, number> = {};
    subscriptions.filter((s) => s.status === "active" && !(s as any).is_trial).forEach((s) => {
      const plan = planLabels[s.plan] || s.plan;
      map[plan] = (map[plan] || 0) + (s.amount || 0);
    });
    return Object.entries(map).map(([name, amount]) => ({ name, amount })).sort((a, b) => b.amount - a.amount);
  }, [subscriptions]);

  const monthlyTrend = useMemo(() => {
    const map: Record<string, { month: string; count: number; revenue: number }> = {};
    subscriptions.forEach((s) => {
      const date = new Date(s.started_at);
      const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
      const label = `${date.getMonth() + 1}/${date.getFullYear()}`;
      if (!map[key]) map[key] = { month: label, count: 0, revenue: 0 };
      map[key].count++;
      map[key].revenue += s.amount || 0;
    });
    return Object.entries(map).sort(([a], [b]) => a.localeCompare(b)).map(([, v]) => v).slice(-12);
  }, [subscriptions]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {/* Plan Distribution */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <PieChartIcon className="w-4 h-4 text-primary" />
            توزيع الخطط
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[280px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={planDist} cx="50%" cy="50%" innerRadius={55} outerRadius={95} dataKey="value" label={({ name, value }) => `${name} (${value})`}>
                  {planDist.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Status Distribution */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-primary" />
            توزيع الحالات
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[280px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={statusDist}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis />
                <Tooltip />
                <Bar dataKey="value" name="عدد" fill="hsl(var(--primary))" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Revenue by Plan */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-primary" />
            الإيرادات حسب الخطة
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[280px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={revenueByPlan} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis dataKey="name" type="category" width={80} tick={{ fontSize: 12 }} />
                <Tooltip formatter={(val: number) => `${val.toLocaleString()} ج.م`} />
                <Bar dataKey="amount" name="الإيرادات" fill="#22c55e" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Monthly Trend */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-primary" />
            اتجاه الاشتراكات الشهري
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[280px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={monthlyTrend}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis />
                <Tooltip formatter={(val: number, name: string) => name === "revenue" ? `${val.toLocaleString()} ج.م` : val} />
                <Area type="monotone" dataKey="count" name="عدد الاشتراكات" stroke="hsl(var(--primary))" fill="hsl(var(--primary))" fillOpacity={0.15} />
                <Area type="monotone" dataKey="revenue" name="الإيرادات" stroke="#22c55e" fill="#22c55e" fillOpacity={0.1} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SubscriptionCharts;
